import { isAnonymousUser, selectUser } from "redux/authReducer";
import { connect, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { State } from "redux/store";
import {
  Dataset,
  DatasetsFilterValue,
  DatasetSortEnum,
  DatasetStatusEnum,
  DatasetTypeEnum,
  EDatasetStatus,
  InventoryFilterData,
} from "types";

import { getDatasets, getIsAddMediaEnabled } from "redux/Datasets/selectors";
import FilterDropdownWrapper from "./FilterDropdownWrapper";
import FilterDropdown from "./FilterDropdown";
import SearchContainer from "./SearchContainer";

import styles from "./style.module.scss";
import uniq from "lodash/uniq";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import { NEW_STATUS } from "../../../../helpers/constants/filters";

interface DatasetsFiltersProps {
  datasetsCountText: string;
  filterData: InventoryFilterData;
  setFilterData: (data: InventoryFilterData) => void;
  handleClearFilters: () => void;
  datasets: Dataset[];
}

type InventoryFilterType =
  | "sortValue"
  | "datasetType"
  | "datasetStatus"
  | "deprecatedDatasetStatus"
  | "datasetJob"
  | "datasetRecipe"
  | "datasetSetup";

const DATASET_TYPES = [
  { id: DatasetTypeEnum.All, text: "All" },
  { id: DatasetTypeEnum.Owned, text: "Owned by me" },
  { id: DatasetTypeEnum.Shared, text: "Shared with me" },
];
const DEPRECATED_BASE_DATASET_STATUS_OPTIONS = [
  { id: DatasetStatusEnum.READY, text: "Ready" },
  { id: DatasetStatusEnum.FATAL_ERROR, text: "Failed" },
  { id: DatasetStatusEnum.INITIALIZING, text: "Resume" },
  { id: DatasetStatusEnum.INDEXING, text: "Indexing" },
  { id: DatasetStatusEnum.UPLOADING, text: "Uploading" },
  { id: DatasetStatusEnum.ENRICHING, text: "Enriching" },
  { id: DatasetStatusEnum.NEW, text: "New" },
];
const BASE_DATASET_STATUS_OPTIONS = [
  { id: EDatasetStatus.READY, text: "Ready" },
  { id: EDatasetStatus.DRAFT, text: "Draft" },
  { id: EDatasetStatus.ERROR, text: "Error" },
  { id: EDatasetStatus.INDEXING, text: "Indexing" },
  { id: EDatasetStatus.READ_ONLY, text: "Read only" },
];

const DEPREACETD_ADD_MEDIA_STATUS_OPTIONS = [
  { id: DatasetStatusEnum.PENDING_INDEX, text: "Pending index" },
  { id: DatasetStatusEnum.UPDATING, text: "Updating" },
];

const ADD_MEDIA_STATUS_OPTIONS = [
  { id: EDatasetStatus.PARTIAL_INDEX, text: "Partial index" },
];

let DEPRECATED_DATASET_STATUS_OPTIONS = DEPRECATED_BASE_DATASET_STATUS_OPTIONS;
let DATASET_STATUS_OPTIONS = BASE_DATASET_STATUS_OPTIONS;
const SORT_OPTIONS = [
  { id: "status", text: "Status" },
  { id: "date", text: "Date Created" },
  { id: "size", text: "Size" },
  { id: "name", text: "Name" },
];

const resetOptions = (
  options: Array<{ id: string }>,
  prev: DatasetsFilterValue[],
) =>
  options.map(({ id }) => ({
    id,
    selected: Boolean(prev.find((v) => v.id === id)?.selected),
  }));

type MultiValuesFilterProps = ReturnType<typeof useGetMultiValuesFilterProps>;

const useGetMultiValuesFilterProps = (
  options: Array<{ id: any; text: string }>,
  values: DatasetsFilterValue[],
) => {
  const [showOptions, setShowOptions] = useState(false);

  const selectedOptions = options.filter((option) =>
    values.find((status) => status.id === option.id && status.selected),
  );
  const filterText = selectedOptions.length > 0 ? selectedOptions[0].text : "";

  const additionalCount =
    selectedOptions.length > 1 ? selectedOptions.length - 1 : 0;

  const selectedOptionsIds = selectedOptions.map(({ id }) => id);

  return {
    showOptions,
    setShowOptions,
    filterText,
    selectedOptionsIds,
    additionalCount,
  };
};

const DatasetsFilters = ({
  filterData,
  setFilterData,
  datasetsCountText,
  handleClearFilters,
  datasets,
}: DatasetsFiltersProps) => {
  const [showDatasetTypeOptions, setShowDatasetTypeOptions] = useState(false);
  const [showSortValueOptions, setShowSortValueOptions] = useState(false);
  const displayNewStatusFilter = useFeatureFlagService(NEW_STATUS).show;

  const {
    datasetStatus,
    deprecatedDatasetStatus,
    datasetType,
    sortValue,
    datasetJob,
    datasetSetup,
    datasetRecipe,
  } = filterData;
  const user = selectUser();
  const isAddMediaEnabled = useSelector(getIsAddMediaEnabled);

  DEPRECATED_DATASET_STATUS_OPTIONS = isAddMediaEnabled
    ? [
        ...DEPRECATED_BASE_DATASET_STATUS_OPTIONS,
        ...DEPREACETD_ADD_MEDIA_STATUS_OPTIONS,
      ]
    : DEPRECATED_BASE_DATASET_STATUS_OPTIONS;

  DATASET_STATUS_OPTIONS = isAddMediaEnabled
    ? [...BASE_DATASET_STATUS_OPTIONS, ...ADD_MEDIA_STATUS_OPTIONS]
    : BASE_DATASET_STATUS_OPTIONS;

  const { jobsOptions, setupOptions, recipeOptions } = useMemo(() => {
    const getValuesOptions = (
      key: "Job Name" | "Setup Name" | "Recipes Name",
    ) =>
      uniq(
        datasets
          .map((d) => d.metadata?.camtek_db_export?.[key])
          .filter(Boolean)
          .flat(),
      ).map((id) => ({ id, text: id })) || [];
    return {
      jobsOptions: getValuesOptions("Job Name"),
      setupOptions: getValuesOptions("Setup Name"),
      recipeOptions: getValuesOptions("Recipes Name"),
    };
  }, [datasets]);

  useEffect(() => {
    setFilterData({
      ...filterData,
      datasetJob: resetOptions(jobsOptions, filterData.datasetJob),
      datasetSetup: resetOptions(setupOptions, filterData.datasetSetup),
      datasetRecipe: resetOptions(recipeOptions, filterData.datasetRecipe),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsOptions, recipeOptions, setupOptions]);

  const deprecatedDatasetStatusParams = useGetMultiValuesFilterProps(
    DEPRECATED_DATASET_STATUS_OPTIONS,
    deprecatedDatasetStatus,
  );
  const datasetStatusParams = useGetMultiValuesFilterProps(
    DATASET_STATUS_OPTIONS,
    datasetStatus,
  );

  const datasetJobParams = useGetMultiValuesFilterProps(
    jobsOptions,
    datasetJob,
  );
  const datasetSetupParams = useGetMultiValuesFilterProps(
    setupOptions,
    datasetSetup,
  );
  const datasetRecipeParams = useGetMultiValuesFilterProps(
    recipeOptions,
    datasetRecipe,
  );

  const datasetTypeFilterText = filterData.datasetType
    ? filterData.datasetType
    : "";
  const sortValueFilterText = SORT_OPTIONS.filter(
    (option) => sortValue === option.id,
  ).map((option) => option.text);

  const handleFilterSelection = (
    id: string | null,
    type: InventoryFilterType,
    filterProps?: MultiValuesFilterProps,
  ) => {
    if (type === "datasetType") {
      if (id !== datasetType) {
        setFilterData({ ...filterData, datasetType: id as DatasetTypeEnum });
      }
      setShowDatasetTypeOptions(false);
    } else if (type === "sortValue") {
      if (id !== sortValue) {
        setFilterData({ ...filterData, sortValue: id as DatasetSortEnum });
      }
      setShowSortValueOptions(false);
    } else {
      const areAllSelected =
        filterProps!.selectedOptionsIds.length === filterData[type].length;
      if (id === "All") {
        const updatedValues = filterData[type].map((type) => {
          return { ...type, selected: areAllSelected ? false : true };
        });

        setFilterData({ ...filterData, [type]: updatedValues });
      } else {
        let updatedValues = filterData[type].map((status) => {
          return {
            ...status,
            selected: status.id === id ? !status.selected : status.selected,
          };
        });

        setFilterData({ ...filterData, [type]: updatedValues });
      }
    }
  };

  return (
    <div className={styles.datasetFilterContainer}>
      <div className={styles.leftContainer}>
        <SearchContainer
          filterData={filterData}
          setFilterData={setFilterData}
        />
        {/* DatasetType Filter */}
        {!(user === null || isAnonymousUser()) && (
          <FilterDropdownWrapper
            title={"Dataset Ownership"}
            filterSelectionText={datasetTypeFilterText}
            showDropdown={showDatasetTypeOptions}
            setShowDropdown={setShowDatasetTypeOptions}
          >
            <FilterDropdown
              selectedOptions={[]}
              options={DATASET_TYPES}
              selectedOption={datasetType}
              handleSelection={(id: string | null) =>
                handleFilterSelection(id, "datasetType")
              }
            />
          </FilterDropdownWrapper>
        )}

        {/* DatasetStatus Filter */}
        {displayNewStatusFilter && (
          <FilterDropdownWrapper
            title={"Dataset Status"}
            filterSelectionText={datasetStatusParams.filterText}
            showDropdown={datasetStatusParams.showOptions}
            setShowDropdown={datasetStatusParams.setShowOptions}
            additionalCount={datasetStatusParams.additionalCount}
          >
            <FilterDropdown
              selectedOption={null}
              allowMultiSelect={true}
              defaultOptionText={"All"}
              options={DATASET_STATUS_OPTIONS}
              selectedOptions={datasetStatusParams.selectedOptionsIds}
              handleSelection={(id: string | null) =>
                handleFilterSelection(id, "datasetStatus", datasetStatusParams)
              }
            />
          </FilterDropdownWrapper>
        )}

        {!displayNewStatusFilter && (
          <FilterDropdownWrapper
            title={"Dataset Status"}
            filterSelectionText={deprecatedDatasetStatusParams.filterText}
            showDropdown={deprecatedDatasetStatusParams.showOptions}
            setShowDropdown={deprecatedDatasetStatusParams.setShowOptions}
            additionalCount={deprecatedDatasetStatusParams.additionalCount}
          >
            <FilterDropdown
              selectedOption={null}
              allowMultiSelect={true}
              defaultOptionText={"All"}
              options={DEPRECATED_DATASET_STATUS_OPTIONS}
              selectedOptions={deprecatedDatasetStatusParams.selectedOptionsIds}
              handleSelection={(id: string | null) =>
                handleFilterSelection(
                  id,
                  "deprecatedDatasetStatus",
                  deprecatedDatasetStatusParams,
                )
              }
            />
          </FilterDropdownWrapper>
        )}

        <FilterDropdownWrapper
          title={"Dataset Job"}
          filterSelectionText={datasetJobParams.filterText}
          showDropdown={datasetJobParams.showOptions}
          setShowDropdown={datasetJobParams.setShowOptions}
          additionalCount={datasetJobParams.additionalCount}
        >
          <FilterDropdown
            showSearch
            selectedOption={null}
            allowMultiSelect={true}
            defaultOptionText={"All"}
            options={jobsOptions}
            selectedOptions={datasetJobParams.selectedOptionsIds}
            handleSelection={(id: string | null) =>
              handleFilterSelection(id, "datasetJob", datasetJobParams)
            }
          />
        </FilterDropdownWrapper>

        <FilterDropdownWrapper
          title={"Dataset Setup"}
          filterSelectionText={datasetSetupParams.filterText}
          showDropdown={datasetSetupParams.showOptions}
          setShowDropdown={datasetSetupParams.setShowOptions}
          additionalCount={datasetSetupParams.additionalCount}
        >
          <FilterDropdown
            showSearch
            selectedOption={null}
            allowMultiSelect={true}
            defaultOptionText={"All"}
            options={setupOptions}
            selectedOptions={datasetSetupParams.selectedOptionsIds}
            handleSelection={(id: string | null) =>
              handleFilterSelection(id, "datasetSetup", datasetSetupParams)
            }
          />
        </FilterDropdownWrapper>

        <FilterDropdownWrapper
          title={"Dataset Recipe"}
          filterSelectionText={datasetRecipeParams.filterText}
          showDropdown={datasetRecipeParams.showOptions}
          setShowDropdown={datasetRecipeParams.setShowOptions}
          additionalCount={datasetRecipeParams.additionalCount}
        >
          <FilterDropdown
            showSearch
            selectedOption={null}
            allowMultiSelect={true}
            defaultOptionText={"All"}
            options={recipeOptions}
            selectedOptions={datasetRecipeParams.selectedOptionsIds}
            handleSelection={(id: string | null) =>
              handleFilterSelection(id, "datasetRecipe", datasetRecipeParams)
            }
          />
        </FilterDropdownWrapper>

        {/* Sort Filter */}
        <FilterDropdownWrapper
          title={"Sort by"}
          filterSelectionText={sortValueFilterText[0]}
          showDropdown={showSortValueOptions}
          setShowDropdown={setShowSortValueOptions}
        >
          <FilterDropdown
            selectedOptions={[]}
            options={SORT_OPTIONS}
            selectedOption={sortValue}
            handleSelection={(id: string | null) =>
              handleFilterSelection(id, "sortValue")
            }
          />
        </FilterDropdownWrapper>

        <div
          onClick={handleClearFilters}
          className={styles.clearSelection}
          data-testid="dataset-inventory-clear-filters-btn"
        >
          Clear Selection
        </div>
      </div>
      <div className={styles.rightContainer}>{datasetsCountText}</div>
    </div>
  );
};

const mapStatesToProps = (state: State) => {
  return {
    datasets: getDatasets(state),
  };
};

export default connect(mapStatesToProps)(DatasetsFilters);
